import React, { useCallback, useMemo } from 'react';
import i18next from 'i18next';

import SimpleTableWithSorting, {
  generateColumn,
  getRTOriginal,
  TableCellProps,
} from 'app/components/common/SimpleTableWithSorting';
import type { ColumnDef } from '@tanstack/react-table';
import { formatDate } from 'utils/date-time';
import type { TCustomer, TCustomerWithSourceAndDevices } from 'types';
import { Input, Label } from 'reactstrap';

type TCustomerType = TCustomer | TCustomerWithSourceAndDevices;

interface CustomersTableProps {
  customers: TCustomerType[];
  onCustomerSelect?: (customer: TCustomerType) => void;
  selectedCustomerIds?: number[];
}

export const CustomersTable: React.FC<CustomersTableProps> = ({
  customers,
  onCustomerSelect,
  selectedCustomerIds,
}) => {
  const renderSendPushNotificationButton = useCallback(
    (customer: TCustomerWithSourceAndDevices) => {
      const validDevices = customer.devices_with_invalid.filter(
        (device) =>
          device.valid_until && new Date(device.valid_until) > new Date()
      );
      return (
        <>
          <div className="small">
            {i18next.t('customers-table.devices-count', { total: customer.devices_with_invalid.length, active: validDevices.length })}
          </div>
        </>
      );
    },
    []
  );

  const columns: ColumnDef<unknown, unknown>[] = useMemo(
    () => [
      generateColumn('id', {
        customCellFormatter: (props: TableCellProps<TCustomer>) => {
          const customer = getRTOriginal(props) as TCustomerType;
          return (
            <>
              <div>
                {onCustomerSelect && selectedCustomerIds ? (
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={selectedCustomerIds.includes(customer.id)}
                      onChange={() => onCustomerSelect(customer)}
                    />
                    <span>{props.getValue()}</span>
                  </Label>
                ) : (
                  props.getValue()
                )}
              </div>
              {'devices_with_invalid' in customer &&
                renderSendPushNotificationButton(customer)}
            </>
          );
        },
      }),
      generateColumn('name'),
      generateColumn('surname'),
      generateColumn('phone'),
      generateColumn('email'),
      generateColumn('created_at', {
        customHeader: 'Created At',
        customCellFormatter: (props) => (
          <div>{!!props.getValue() ? formatDate(new Date(props.getValue())) : '-'}</div>
        ),
      }),
      generateColumn('updated_at', {
        customHeader: 'Updated At',
        customCellFormatter: (props) => (
          <div>{!!props.getValue() ? formatDate(new Date(props.getValue())) : '-'}</div>
        ),
      }),
    ],
    [onCustomerSelect, renderSendPushNotificationButton, selectedCustomerIds]
  );

  return (
    <SimpleTableWithSorting
      columns={columns}
      data={customers}
      hasFilters
      pageSize={50}
    />
  );
};

export default CustomersTable;
